<template>
  <div class="tw-bg-whitex tw-rounded-lgx tw-borderx tw-border-gray-200x tw-p-6x">
    <!-- Header -->
    <div v-if="false" class="tw-flex tw-items-center tw-justify-between tw-mb-6">
      <div >
        <h2 class="tw-text-xl tw-font-semibold tw-text-gray-900 tw-mb-1">
          {{ title }}
        </h2>
        <p class="tw-text-sm tw-text-gray-600">
          {{ sessions.length }} event{{ sessions.length !== 1 ? 's' : '' }} found
        </p>
      </div>
      
      <!-- View Toggle (if needed) -->
      <div v-if="showViewToggle && false" class="tw-flex tw-items-center tw-gap-2">
        <a-button
          @click="viewMode = 'grid'"
          :intent="viewMode === 'grid' ? 'primary' : 'secondary'"
          size="sm"
          left-icon="solar:widget-4-bold-duotone"
        >
          Grid
        </a-button>
        <a-button
          @click="viewMode = 'list'"
          :intent="viewMode === 'list' ? 'primary' : 'secondary'"
          size="sm"
          left-icon="solar:list-bold"
        >
          List
        </a-button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-4">
      <SessionLoadingSkeleton v-for="i in 8" :key="i" />
    </div>

    <!-- Empty State -->
    <SessionEmptyState 
      v-else-if="!loading && sessions.length === 0"
      :category="category"
      :action-text="emptyStateActionText"
      :action-handler="emptyStateActionHandler"
      @action="handleEmptyStateAction"
    />

    <!-- Sessions Grid/List -->
    <div v-else>
      <!-- Grid View -->
      <div v-if="viewMode === 'grid'" class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-4">
        <SessionCard
          v-for="session in displayedSessions"
          :key="session.id"
          :session="session"
          :show-join-button="props.category !== 'past' && props.category !== 'all' || (props.category === 'all' && !isPastSession(session))"
          :loading="props.loadingSessions.has(session.id)"
          @join="handleSessionJoin"
          @view="handleSessionView"
        />
      </div>

      <!-- List View -->
      <div v-else class="tw-space-y-4">
        <div
          v-for="session in displayedSessions"
          :key="session.id"
          class="tw-border tw-border-gray-200 tw-rounded-lg tw-p-4 hover:tw-bg-gray-50 tw-transition-colors"
        >
          <div class="tw-flex tw-items-start tw-gap-4">
            <!-- Session Image -->
            <div class="tw-flex-shrink-0 tw-w-24 tw-h-24 tw-rounded-lg tw-overflow-hidden tw-bg-gray-100 tw-flex tw-items-center tw-justify-center tw-border tw-border-gray-200">
              <img 
                v-if="session.image" 
                :src="session.image" 
                :alt="session.name" 
                class="tw-w-full tw-h-full tw-object-cover"
              />
              <icon 
                v-else 
                :icon="getSessionTypeIcon(session.type)" 
                class="tw-text-2xl tw-text-gray-400"
              />
            </div>
            
            <!-- Session Content -->
            <div class="tw-flex-1 tw-min-w-0">
              <div class="tw-flex tw-items-start tw-justify-between tw-mb-2">
                <div class="tw-flex tw-items-center tw-gap-2">
                  <span :class="[
                    'tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-rounded-full',
                    getStatusBadgeClass(session.status)
                  ]">
                    {{ session.status }}
                  </span>
                  <span :class="[
                    'tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-rounded-full',
                    getTypeBadgeClass(session.type)
                  ]">
                    {{ session.type }}
                  </span>
                </div>
              </div>
              
              <h3 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-2">
                {{ session.name }}
              </h3>
              
              <p class="tw-text-sm tw-text-gray-600 tw-mb-3 tw-line-clamp-2">
                {{ session.description }}
              </p>
              
              <div class="tw-flex tw-items-center tw-gap-4 tw-text-sm tw-text-gray-600">
                <div class="tw-flex tw-items-center tw-gap-1">
                  <icon icon="solar:calendar-bold-duotone" class="tw-text-gray-400" />
                  <span>{{ formatSessionDate(session.start_date, session.start_time) }}</span>
                </div>
                <div class="tw-flex tw-items-center tw-gap-1">
                  <icon icon="solar:clock-circle-bold-duotone" class="tw-text-gray-400" />
                  <span>{{ formatDuration(session.start_time, session.end_time) }}</span>
                </div>
              </div>
            </div>
            
                         <!-- Actions -->
             <div class="tw-flex tw-items-center tw-gap-2">
               <a-button 
                 v-if="props.category !== 'past' && props.category !== 'all' || (props.category === 'all' && !isPastSession(session))"
                 @click="handleSessionJoin(session)"
                 :loading="props.loadingSessions.has(session.id)"
                 :disabled="props.loadingSessions.has(session.id)"
                 :intent="session.is_joined ? 'danger' : 'primary'"
                 size="sm"
               >
                 <icon :icon="session.is_joined ? 'solar:minus-circle-bold' : 'solar:plus-circle-bold'" class="tw-mr-1" />
                 {{ session.is_joined ? 'Leave' : 'Join' }}
               </a-button>
               <a-button 
                 @click="handleSessionView(session)"
                 :intent="(props.category === 'past' || (props.category === 'all' && isPastSession(session))) ? 'primary' : 'secondary'"
                 size="sm"
               >
                 <icon icon="solar:eye-bold" class="tw-mr-1" />
                 View
               </a-button>
             </div>
          </div>
        </div>
      </div>

      <!-- Show More Button -->
      <div v-if="hasMore && sessions.length > 0" class="tw-flex tw-justify-center tw-mt-6">
        <a-button 
          @click="handleLoadMore"
          intent="secondary"
          :loading="loadingMore"
        >
          <icon icon="solar:arrow-down-bold" class="tw-mr-1" />
          Show More
        </a-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue'
import moment from 'moment'
import SessionCard from './SessionCard.vue'
import SessionLoadingSkeleton from './SessionLoadingSkeleton.vue'
import SessionEmptyState from './SessionEmptyState.vue'

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  sessions: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  },
  hasMore: {
    type: Boolean,
    default: false
  },
  category: {
    type: String,
    required: true,
    validator: (value) => ['today', 'upcoming', 'past', 'all'].includes(value)
  },
  showViewToggle: {
    type: Boolean,
    default: true
  },
  emptyStateActionText: {
    type: String,
    default: ''
  },
  emptyStateActionHandler: {
    type: Function,
    default: null
  },
  loadingSessions: {
    type: Set,
    default: () => new Set()
  }
})

const emit = defineEmits(['load-more', 'session-join', 'session-view', 'empty-state-action'])

// Reactive data
const viewMode = ref('grid')
const loadingMore = ref(false)

// Computed properties
const displayedSessions = computed(() => {
  return props.sessions
})

// Helper functions
const formatSessionDate = (date, time) => {
  if (!date) return ''
  
  const dateObj = moment(`${date} ${time}`, 'YYYY-MM-DD HH:mm')
  const today = moment()
  const tomorrow = moment().add(1, 'day')
  
  if (dateObj.isSame(today, 'day')) {
    return `Today at ${dateObj.format('h:mm A')}`
  } else if (dateObj.isSame(tomorrow, 'day')) {
    return `Tomorrow at ${dateObj.format('h:mm A')}`
  } else {
    return dateObj.format('MMM D, YYYY at h:mm A')
  }
}

const formatDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return ''
  
  const start = moment(startTime, 'HH:mm')
  const end = moment(endTime, 'HH:mm')
  const duration = moment.duration(end.diff(start))
  
  const hours = Math.floor(duration.asHours())
  const minutes = duration.minutes()
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }
  return `${minutes}m`
}

const getSessionTypeIcon = (type) => {
  const typeIcons = {
    'Meeting': 'solar:users-group-rounded-bold-duotone',
    'Webinar': 'solar:monitor-bold-duotone',
    'Conference': 'solar:buildings-3-bold-duotone',
    'Workshop': 'solar:widget-4-bold-duotone',
    'Presentation': 'solar:presentation-graph-bold-duotone'
  }
  return typeIcons[type] || 'solar:calendar-bold-duotone'
}

const getStatusBadgeClass = (status) => {
  const statusClasses = {
    'scheduled': 'tw-bg-blue-100 tw-text-blue-700',
    'live': 'tw-bg-red-100 tw-text-red-700',
    'completed': 'tw-bg-green-100 tw-text-green-700',
    'cancelled': 'tw-bg-gray-100 tw-text-gray-700'
  }
  return statusClasses[status] || 'tw-bg-gray-100 tw-text-gray-700'
}

const getTypeBadgeClass = (type) => {
  const typeClasses = {
    'Meeting': 'tw-bg-purple-100 tw-text-purple-700',
    'Webinar': 'tw-bg-indigo-100 tw-text-indigo-700',
    'Conference': 'tw-bg-blue-100 tw-text-blue-700',
    'Workshop': 'tw-bg-orange-100 tw-text-orange-700',
    'Presentation': 'tw-bg-teal-100 tw-text-teal-700'
  }
  return typeClasses[type] || 'tw-bg-gray-100 tw-text-gray-700'
}

// Helper function to determine if a session is in the past
const isPastSession = (session) => {
  if (!session.start_date || !session.end_time) return false
  
  const now = moment()
  const sessionEnd = moment(`${session.start_date} ${session.end_time}`, 'YYYY-MM-DD HH:mm')
  return now.isAfter(sessionEnd)
}

// Event handlers
const handleLoadMore = async () => {
  loadingMore.value = true
  emit('load-more', props.category)
  loadingMore.value = false
}

const handleSessionJoin = (session) => {
  emit('session-join', session)
}

const handleSessionView = (session) => {
  emit('session-view', session)
}

const handleEmptyStateAction = (category) => {
  emit('empty-state-action', category)
}
</script>

<style scoped>
.tw-line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style> 